import { Instagram, Facebook, Phone, MapPin, Clock } from "lucide-react";
import { DOORDASH_URL, ASSETS } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";

export default function Footer() {
  const { address, phone, hours, footer_tagline } = useSettings();
  return (
    <footer
      data-testid="trio-footer"
      className="bg-[var(--trio-text)] text-[var(--trio-bg)] py-20"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-5">
            <img
              src={ASSETS.logo}
              alt="Trio — Eat. Drink. Vibe."
              className="h-20 sm:h-24 w-auto invert"
              data-testid="footer-logo"
            />
            <p className="mt-6 text-[var(--trio-bg)]/70 max-w-sm text-base leading-relaxed">
              {footer_tagline} A neighborhood plate and a late-night lounge in
              the heart of Huntsville.
            </p>
            <div className="mt-8 flex gap-3">
              <a
                href="https://www.instagram.com/triohsv/"
                target="_blank"
                rel="noreferrer"
                data-testid="social-instagram"
                aria-label="Trio on Instagram — @triohsv"
                className="w-10 h-10 border border-[var(--trio-bg)]/30 hover:border-[var(--trio-accent)] hover:text-[var(--trio-accent)] flex items-center justify-center transition-colors"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href="https://www.facebook.com/share/17jHJeBudX/?mibextid=wwXIfr"
                target="_blank"
                rel="noreferrer"
                data-testid="social-facebook"
                aria-label="Trio on Facebook"
                className="w-10 h-10 border border-[var(--trio-bg)]/30 hover:border-[var(--trio-accent)] hover:text-[var(--trio-accent)] flex items-center justify-center transition-colors"
              >
                <Facebook className="w-4 h-4" />
              </a>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-accent)] font-bold mb-4">
              Visit
            </div>
            <div className="flex items-start gap-3 text-sm text-[var(--trio-bg)]/80 mb-3">
              <MapPin className="w-4 h-4 mt-0.5" />
              <span>{address}</span>
            </div>
            <div className="flex items-start gap-3 text-sm text-[var(--trio-bg)]/80">
              <Phone className="w-4 h-4 mt-0.5" />
              <a href={`tel:${phone}`} data-testid="footer-phone">{phone}</a>
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-accent)] font-bold mb-4 flex items-center gap-2">
              <Clock className="w-3 h-3" /> Hours
            </div>
            <ul className="space-y-2 text-sm">
              {hours.map((h, i) => (
                <li
                  key={i}
                  className="flex justify-between gap-6 text-[var(--trio-bg)]/80"
                >
                  <span>{h.day}</span>
                  <span>{h.time}</span>
                </li>
              ))}
            </ul>
            <a
              href={DOORDASH_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-doordash"
              className="mt-6 inline-block bg-[var(--trio-accent)] hover:bg-[var(--trio-primary)] text-white px-6 py-3 text-xs tracking-widest uppercase transition-colors"
            >
              Order on DoorDash
            </a>
          </div>
        </div>

        <div className="pt-8 border-t border-[var(--trio-bg)]/20 flex flex-wrap items-center justify-between gap-4 text-xs text-[var(--trio-bg)]/50">
          <span>© {new Date().getFullYear()} Trio. All rights reserved.</span>
          <div className="flex items-center gap-5">
            <a
              href="/admin/login"
              data-testid="footer-admin-link"
              className="uppercase tracking-widest hover:text-[var(--trio-accent)] transition-colors"
            >
              Owner Login
            </a>
            <span className="font-display uppercase tracking-widest">Eat. Drink. Vibe.</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
